import React, { useEffect, useState, useRef, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShieldAlert, Brain, Flame, EyeOff, CheckCircle2, XCircle, BookOpen, Clock, Sparkles, ChevronDown, Lock, Star, ShieldCheck, ArrowDown } from 'lucide-react';
import { Button } from '../components/Button';
import { trackViewContent, handlePurchaseCTA } from '../utils/metaPixel';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-brown/10 py-5" data-id="element-3">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left focus:outline-none group" data-id="element-4">
        <span className="font-serif text-xl md:text-2xl text-brown pr-4 group-hover:text-terracotta transition-colors" data-id="element-5">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-6">
          <ChevronDown className="w-6 h-6 text-terracotta flex-shrink-0" data-id="element-7" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-8">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-9">
            <p className="pt-4 text-brown/80 text-lg leading-relaxed" data-id="element-10">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function LandingPage() {
  const pricingRef = useRef<HTMLDivElement>(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [pricingVisible, setPricingVisible] = useState(false);
  useEffect(() => {
    if (!pricingRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        trackViewContent();
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);
  // Observer to detect when pricing section is visible (to hide sticky CTA)
  useEffect(() => {
    if (!pricingRef.current) return;
    const observer = new IntersectionObserver(entries => {
      setPricingVisible(entries[0].isIntersecting);
    }, {
      threshold: 0.1
    });
    observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-cream overflow-hidden" data-id="element-11">
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-12 md:pt-12 md:pb-12 px-6 overflow-hidden" data-id="element-12">
        <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-13">
          <img src="https://cdn.magicpatterns.com/uploads/8g3wmPGEMmZfgspTMBkDC1/Imagen_fondo_Hero.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-50" fetchPriority="high" loading="eager" data-id="element-14" />
          <div className="absolute inset-0 bg-cream/50" data-id="element-15" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center text-center gap-10" data-id="element-16">
          <motion.div className="space-y-8 flex flex-col items-center" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-17">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-sage font-bold text-sm border border-sage/40 shadow-md" data-id="element-18">
              <Brain className="w-4 h-4" data-id="element-19" />
              <span data-id="element-20">Basado en Neurociencia y Regulación Emocional</span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-[3.5rem] text-brown leading-[1.15]" data-id="element-21">
              Descubre cómo calmar las rabietas de tu hijo en{' '}
              <span className="text-terracotta italic underline decoration-terracotta/30 underline-offset-4" data-id="element-22">
                menos de 5 minutos
              </span>{' '}
              — sin gritos, castigos ni culpa.
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-brown/70 leading-relaxed max-w-2xl" data-id="element-23">
              Con el{' '}
              <strong className="text-terracotta font-bold" data-id="element-24">
                Método de Regulación Co-activa
              </strong>{' '}
              (basado en neurociencia), recuperarás la calma en casa y la
              conexión con tu peque 💛
            </motion.p>

            <motion.div variants={fadeInUp} className="pt-2 flex flex-col items-center gap-5 w-full" data-id="element-25">
              <button className="w-full max-w-md py-5 text-xl font-bold text-white rounded-2xl shadow-lg transition-all duration-200 hover:brightness-110 active:scale-[0.98]" style={{
              backgroundColor: '#2b8a3e'
            }} onClick={() => document.getElementById('pricing')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-26">
                Obtener Acceso Inmediato
              </button>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-brown/50" data-id="element-27">
                <span className="flex items-center gap-1.5" data-id="element-28">
                  <Clock className="w-4 h-4" data-id="element-29" /> Acceso inmediato
                </span>
                <span className="flex items-center gap-1.5" data-id="element-30">
                  <Lock className="w-4 h-4" data-id="element-31" /> Pago seguro
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="w-full max-w-sm relative mt-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} data-id="element-32">
            <div className="absolute inset-0 bg-terracotta/8 rounded-3xl blur-2xl scale-110 -z-10" data-id="element-33" />
            <img src="https://cdn.magicpatterns.com/uploads/2GYcV6imNUPXYt69p1MPH8/Imagen.Hero.webp" alt="El Fin de las Rabietas - Guía para padres en tablet" className="w-full h-auto rounded-2xl shadow-soft object-cover" fetchPriority="high" loading="eager" data-id="element-34" />
          </motion.div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-12 px-6 bg-white" data-id="element-35">
        <motion.div className="max-w-3xl mx-auto text-center space-y-8" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-36">
          <motion.div variants={fadeInUp} data-id="element-37">
            <Heart className="w-12 h-12 text-terracotta mx-auto mb-6 opacity-80" data-id="element-38" />
            <p className="text-xl md:text-2xl leading-relaxed text-brown font-medium" data-id="element-39">
              "Son las 6 de la tarde. Estás agotada. Y de repente, por un vaso
              de color equivocado o un juguete que no se queda en pie, tu hijo
              estalla. Los gritos llenan la casa, tu paciencia se evapora y
              sientes que, por más que explicas o castigas, nada cambia."
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-16 h-px bg-terracotta/30 mx-auto" data-id="element-40" />

          <motion.p variants={fadeInUp} className="text-lg text-brown/80 leading-relaxed" data-id="element-41">
            No estás sola. Pero lo más importante que leerás hoy es esto: las
            rabietas no son una señal de que eres una "mala madre", son una
            desesperada señal de auxilio del cerebro en desarrollo de tu hijo
            que aún no sabe cómo decirte lo que siente.
          </motion.p>
        </motion.div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-42">
        <div className="max-w-4xl mx-auto" data-id="element-43">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft" data-id="element-44">
            <h2 className="text-3xl md:text-4xl text-center mb-8" data-id="element-45">
              El ciclo de "reacción-culpa"
            </h2>
            <div className="space-y-6 text-lg text-brown/80 leading-relaxed" data-id="element-46">
              <p data-id="element-47">
                La mayoría de las madres viven en un ciclo de "reacción-culpa".
                Intentas razonar con lógica cuando él está en plena crisis (lo
                cual es neurológicamente imposible), terminas gritando porque no
                puedes más, y luego pasas la noche mirando a tu hijo dormir
                sintiéndote la peor persona del mundo.
              </p>
              <p data-id="element-48">
                El problema no es tu falta de autoridad, sino que nos enseñaron
                a criar desde la supresión de emociones ("no llores", "vete a tu
                cuarto", "ahora te vas a enterar"). Esos métodos no enseñan a tu
                hijo a calmarse; solo le enseñan a esconder su dolor por miedo,
                lo que garantiza que la rabieta sea más explosiva la próxima
                vez.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-12 px-6 bg-white" data-id="element-49">
        <div className="max-w-6xl mx-auto" data-id="element-50">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-51">
            <h2 className="text-3xl md:text-5xl mb-6" data-id="element-52">
              Por qué lo que has intentado no funciona
            </h2>
            <p className="text-xl text-brown/70 max-w-2xl mx-auto" data-id="element-53">
              No es tu culpa. Las estrategias tradicionales van en contra de
              cómo funciona el cerebro infantil.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-54">
            {[{
            icon: Brain,
            title: 'Intentar razonar',
            desc: "Falla porque el cerebro lógico de tu hijo se 'desconecta' literalmente durante la crisis; es como hablarle a alguien que se está ahogando."
          }, {
            icon: Flame,
            title: 'Gritar',
            desc: 'Solo añade combustible al fuego, elevando los niveles de cortisol y bloqueando cualquier posibilidad de aprendizaje.'
          }, {
            icon: ShieldAlert,
            title: 'Los castigos',
            desc: 'Generan resentimiento y miedo, pero no construyen las rutas neuronales que tu hijo necesita para el autocontrol futuro.'
          }, {
            icon: EyeOff,
            title: 'Ignorarlo',
            desc: "Solo le confirma que su 'puerto seguro' (tú) desaparece cuando él más te necesita, aumentando su ansiedad."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-cream p-8 rounded-2xl flex gap-6 items-start" data-id="element-55">
                <div className="bg-white p-4 rounded-full shadow-sm shrink-0" data-id="element-56">
                  <item.icon className="w-8 h-8 text-terracotta" data-id="element-57" />
                </div>
                <div data-id="element-58">
                  <h3 className="text-2xl mb-3" data-id="element-59">{item.title}</h3>
                  <p className="text-brown/70 leading-relaxed" data-id="element-60">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM (Dark Section) */}
      <section className="py-12 px-6 bg-brown text-cream relative overflow-hidden" data-id="element-61">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-terracotta/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-62" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-63">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-64">
            <Sparkles className="w-12 h-12 text-terracotta mx-auto mb-8" data-id="element-65" />
            <h2 className="text-4xl md:text-5xl mb-8 text-white" data-id="element-66">
              Presentamos el Método de los 5 Minutos <br data-id="element-67" />
              <span className="text-terracotta italic text-3xl md:text-4xl" data-id="element-68">
                (Regulación Co-activa)
              </span>
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-cream/80 leading-relaxed font-light" data-id="element-69">
              <p data-id="element-70">No es magia, es neurociencia aplicada a la crianza.</p>
              <p data-id="element-71">
                En lugar de luchar contra la rabieta, este sistema te enseña a
                usar tu propio sistema nervioso como un "ancla" para el de tu
                hijo. A través de una secuencia exacta de 60 segundos por etapa{' '}
                <strong className="text-white font-medium" data-id="element-72">
                  (Pausar, Conectar, Validar, Redirigir y Cerrar)
                </strong>
                , lograrás que el cerebro de tu hijo pase del modo
                "supervivencia" al modo "calma" en tiempo récord.
              </p>
              <p className="text-2xl text-white font-serif pt-4" data-id="element-73">
                No fuerzas la obediencia; facilitas la regulación.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-12 px-6 bg-cream" data-id="element-74">
        <div className="max-w-5xl mx-auto" data-id="element-75">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-76">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-77">
              ¿Qué incluye el método?
            </h2>
            <p className="text-xl text-brown/70" data-id="element-78">
              Todo lo que necesitas para transformar el ambiente de tu hogar hoy
              mismo.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-79">
            {[{
            title: "La Guía Maestra 'Fin de las Rabietas'",
            desc: 'Un paso a paso completo desde la neurociencia hasta la acción práctica.'
          }, {
            title: 'El Mapa de los 5 Minutos',
            desc: 'La secuencia exacta para desescalar cualquier crisis sin importar el lugar.'
          }, {
            title: 'Diccionario de Frases de Validación',
            desc: "Qué decir exactamente para 'nombrar y domesticar' la emoción sin usar el destructivo 'pero...'."
          }, {
            title: 'Protocolo de Emergencia en Público',
            desc: 'Cómo manejar la presión social y el juicio de los extraños sin ceder ante el berrinche.'
          }, {
            title: 'Guía de Reparación',
            desc: 'El plan de 4 pasos para cuando tú pierdes el control y necesitas sanar el vínculo con tu hijo.'
          }, {
            title: 'Estrategia de Prevención',
            desc: "Rutinas sencillas y el método 'una vez y se cumple' para reducir la frecuencia de las rabietas antes de que ocurran."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-2xl shadow-sm border border-brown/5 flex gap-4" data-id="element-80">
                <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-1" data-id="element-81" />
                <div data-id="element-82">
                  <h4 className="font-serif text-xl mb-2" data-id="element-83">{item.title}</h4>
                  <p className="text-brown/70" data-id="element-84">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-12 px-6 bg-white" data-id="element-85">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-86">
          {/* For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-sage/10 p-8 md:p-10 rounded-3xl" data-id="element-87">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-88">
              <Heart className="w-8 h-8 text-sage" data-id="element-89" />
              Esto ES para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-90">
              {['Madres con hijos entre 2 y 7 años que se sienten sobrepasadas por la intensidad emocional de sus pequeños.', 'Cuidadores que quieren romper ciclos generacionales de gritos y castigos físicos.', 'Mamás que trabajan y llegan a casa con poca energía, necesitando soluciones efectivas y rápidas.', 'Padres que buscan una crianza basada en el respeto pero que no quieren caer en la permisividad.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-91">
                  <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-0.5" data-id="element-92" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-93">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-terracotta/5 p-8 md:p-10 rounded-3xl border border-terracotta/10" data-id="element-94">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-95">
              <XCircle className="w-8 h-8 text-terracotta" data-id="element-96" />
              Esto NO es para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-97">
              {["Personas que buscan 'trucos' para manipular o silenciar a los niños rápidamente por miedo.", 'Quienes creen que el castigo físico es la única forma de enseñar respeto.', 'Madres que no están dispuestas a trabajar primero en su propia calma antes de pedirla a sus hijos.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-98">
                  <XCircle className="w-6 h-6 text-terracotta/60 shrink-0 mt-0.5" data-id="element-99" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-100">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. HOW IT WORKS & PROOF */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-101">
        <div className="max-w-4xl mx-auto space-y-20" data-id="element-102">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-103">
            <h2 className="text-4xl text-center mb-12" data-id="element-104">¿Cómo funciona?</h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-brown/10" data-id="element-105">
              {[{
              step: '1',
              title: 'Descarga la Guía',
              desc: 'Recibe acceso inmediato para leerla desde tu celular o tablet hoy mismo.'
            }, {
              step: '2',
              title: 'Aplica el Primer Minuto',
              desc: 'La próxima vez que empiece el caos, aplica la técnica de respiración y pausa que aprenderás en el capítulo 11.'
            }, {
              step: '3',
              title: 'Observa el Cambio',
              desc: 'Mira cómo, al cambiar tu respuesta, la frecuencia y duración de las rabietas disminuye drásticamente en menos de 7 días.'
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-106">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream-dark bg-terracotta text-white font-serif text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-107">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm" data-id="element-108">
                    <h4 className="font-serif text-2xl mb-2" data-id="element-109">{item.title}</h4>
                    <p className="text-brown/70" data-id="element-110">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft text-center" data-id="element-111">
            <Star className="w-10 h-10 text-terracotta mx-auto mb-6 fill-terracotta/20" data-id="element-112" />
            <p className="text-xl md:text-2xl text-brown leading-relaxed italic font-serif" data-id="element-113">
              "En solo una semana aplicando la regulación co-activa, notarás que
              las explosiones de 20 minutos se convierten en momentos de
              conexión de 5 minutos. No solo las rabietas se detienen más
              rápido; notarás que tu hijo empieza a buscarte para contarte lo
              que siente antes de estallar, porque ahora sabe que tú eres un
              lugar seguro, no un juez."
            </p>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-6 bg-white" data-id="element-114">
        <div className="max-w-6xl mx-auto" data-id="element-115">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-116">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-117">
              Lo que dicen otras familias
            </h2>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-118">
            {[{
            name: 'Jessica M.',
            location: 'Houston, TX',
            image: 'https://cdn.magicpatterns.com/uploads/dQDS51MCMuVUPLbVcK2XRi/Jessica_M..webp',
            quote: 'Ay, no saben lo que era mi casa a las 6 de la tarde… un campo de batalla. Mi nena de 3 años se tiraba al piso por TODO. Yo terminaba gritando y después llorando en el baño sintiéndome la peor mamá. Compré esta guía sin mucha esperanza, pero el truco de los 5 minutos me cambió la vida. Ayer mi hija empezó con una rabieta por las galletas y en vez de explotar yo, hice lo que dice el paso 2. Se calmó en menos de 3 minutos. TRES. Yo no lo podía creer. 💛',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Daniela P.',
            location: 'Miami, FL',
            image: 'https://cdn.magicpatterns.com/uploads/m2UocvJukjFXM6UzfC1LNE/Daniela_P..webp',
            quote: 'Les soy honesta, yo era de las que pensaba "a mí me criaron con chancleta y salí bien". Pero ver a mi hijo de 4 años temblando de miedo cuando yo gritaba me rompió el corazón. Una amiga me pasó esta guía y la leí esa misma noche llorando. Lo que más me ayudó fue entender POR QUÉ mi hijo hace lo que hace. No es manipulación, es que su cerebrito no puede más. Ahora cuando empieza una crisis, yo respiro primero y él se calma solito. Mi esposo no lo puede creer.',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Adriana L.',
            location: 'Los Angeles, CA',
            image: 'https://cdn.magicpatterns.com/uploads/fxW4GQ5UtkqDyCTPYK64LC/Adriana_L..webp',
            quote: 'Trabajo doble turno y llego a mi casa destruida. Lo último que necesito es una hora de gritos con mi hijo de 5 años. Esta guía es corta, va directo al punto y las frases que te dan para decirle al niño son INCREÍBLES. Yo antes decía "ya cállate" y ahora digo algo completamente diferente y la reacción de mi hijo es otra. En una semana las rabietas bajaron como un 70%. No es magia, pero se siente como magia. Se las recomiendo a todas las mamás que conozco.',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Carla J.',
            location: 'San Antonio, TX',
            image: 'https://cdn.magicpatterns.com/uploads/cm5YjnwwMpf6TCREGZ2Zrf/Carla_J..webp',
            quote: "Pensé que nada funcionaría con mi hijo porque tiene una personalidad muy fuerte y las rabietas eran extremas, incluso en público. Usé la Estrategia de Emergencia en el supermercado la semana pasada y la diferencia fue increíble. No hubo drama, no hubo miradas de juicio de extraños, solo una conexión real entre nosotros. Si crees que tu hijo es 'imposible', prueba este método.",
            stars: '⭐⭐⭐⭐⭐'
          }].map((t, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl shadow-sm border border-brown/5 flex flex-col gap-6" data-id="element-119">
                <div className="flex items-center gap-4" data-id="element-120">
                  <img src={t.image} alt={t.name} className="w-16 h-16 rounded-full object-cover" loading="lazy" data-id="element-121" />
                  <div data-id="element-122">
                    <h4 className="font-serif text-xl text-brown" data-id="element-123">{t.name}</h4>
                    <p className="text-sm text-brown/60" data-id="element-124">{t.location}</p>
                    <div className="text-sm mt-1" data-id="element-125">{t.stars}</div>
                  </div>
                </div>
                <p className="text-brown/80 leading-relaxed italic" data-id="element-126">
                  "{t.quote}"
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-12 px-6 bg-cream" data-id="element-127">
        <div className="max-w-3xl mx-auto" data-id="element-128">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-129">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-130">Preguntas Frecuentes</h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-white p-6 md:p-10 rounded-3xl shadow-sm" data-id="element-131">
            <FAQItem question="¿Funciona con niños de 2 años?" answer="Sí, está diseñado específicamente para la etapa de desarrollo donde el cerebro emocional es dominante." data-id="element-132" />
            <FAQItem question="¿Qué pasa si mi hijo es muy terco?" answer="No existe la terquedad, existe la necesidad de autonomía. El método enseña cómo darles control sin ceder en tus límites." data-id="element-133" />
            <FAQItem question="¿Y si yo soy la que siempre pierde los nervios?" answer="La guía incluye una sección completa de 'Autocontrol para Mamás' para que tú también sanes tu sistema nervioso." data-id="element-134" />
            <FAQItem question="¿Es mucho texto para leer?" answer="No. Está diseñado para madres con tiempo limitado, con tablas, listas y puntos clave para ir directo a la solución." data-id="element-135" />
          </motion.div>
        </div>
      </section>

      {/* 10. OFFER & CTA */}
      <section ref={pricingRef} id="pricing" className="py-12 px-6 bg-white relative overflow-hidden" data-id="element-136">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cream-dark via-white to-white -z-10" data-id="element-137" />

        <div className="max-w-4xl mx-auto text-center" data-id="element-138">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-8" data-id="element-139">
            <h2 className="text-4xl md:text-5xl leading-tight" data-id="element-140">
              Transforma la atmósfera de tu hogar hoy mismo.
            </h2>

            <div className="bg-cream-dark p-10 md:p-14 rounded-3xl border border-brown/10 shadow-xl shadow-brown/5 max-w-2xl mx-auto relative" data-id="element-141">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-sage text-white px-6 py-2 rounded-full text-sm font-bold tracking-wider uppercase shadow-md" data-id="element-142">
                Oferta de Lanzamiento
              </div>

              <div className="flex flex-col items-center gap-3 mt-6 mb-10" data-id="element-143">
                <span className="text-xl text-brown/35 line-through font-serif" data-id="element-144">
                  $27.00 USD
                </span>
                <div className="flex items-baseline gap-2" data-id="element-145">
                  <span className="text-6xl md:text-7xl font-serif font-bold" style={{
                  color: '#2b8a3e'
                }} data-id="element-146">
                    $6.39
                  </span>
                  <span className="text-xl text-brown/50 font-medium" data-id="element-147">USD</span>
                </div>
              </div>

              <p className="text-lg text-brown/70 mb-10 font-medium tracking-wide" data-id="element-148">
                Pago único. Acceso de por vida.
              </p>

              <p className="text-brown/60 mb-12 leading-relaxed max-w-lg mx-auto" data-id="element-149">
                Obtendrás todas las herramientas para que las tardes vuelvan a
                ser de paz y no de batallas. Es menos de lo que cuesta un café
                especial, pero con un impacto que durará toda la vida de tu
                hijo.
              </p>

              <button className="w-full px-8 py-6 md:py-7 text-xl font-bold text-white rounded-2xl shadow-lg transition-all duration-200 hover:brightness-110 active:scale-[0.98] text-center" style={{
              backgroundColor: '#2b8a3e'
            }} onClick={handlePurchaseCTA} data-id="element-150">
                QUIERO EL MÉTODO FIN DE LAS RABIETAS AHORA
              </button>

              <div className="mt-10 flex flex-col items-center gap-3 p-5 bg-white/80 rounded-2xl border border-sage/15" data-id="element-151">
                <ShieldCheck className="w-9 h-9 text-sage/80" data-id="element-152" />
                <p className="text-brown/80 font-serif text-base font-medium" data-id="element-153">
                  Garantía de Satisfacción — 7 días
                </p>
                <p className="text-brown/50 text-sm text-center leading-relaxed max-w-sm" data-id="element-154">
                  Si no ves resultados aplicando el método, te devolvemos el
                  100% de tu dinero. Sin preguntas.
                </p>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-brown/40" data-id="element-155">
                <Lock className="w-4 h-4" data-id="element-156" />
                <span data-id="element-157">Pago 100% seguro. Acceso inmediato tras la compra.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center text-brown/40 text-sm bg-white border-t border-brown/5 pb-24 md:pb-8" data-id="element-158">
        <p data-id="element-159">
          © {new Date().getFullYear()} El Fin de las Rabietas. Todos los
          derechos reservados.
        </p>
      </footer>

      {/* STICKY FLOATING CTA */}
      <AnimatePresence data-id="element-160">
        {showStickyCTA && !pricingVisible && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/95 backdrop-blur-sm border-t border-brown/10 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] md:hidden" data-id="element-161">
            <button className="w-full py-4 text-lg font-bold text-white rounded-xl shadow-lg transition-all duration-200 hover:brightness-110 active:scale-[0.98]" style={{
          backgroundColor: '#2b8a3e'
        }} onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} data-id="element-162">
              QUIERO EL ACCESO AL MÉTODO
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}